// Comandos do agregado Perfil (issue #71) — criar, editar (nome + Permissão
// de Módulo), inativar/reativar. O executor recebe `ctx.usuarioAtual` e
// impede editar OU inativar o PRÓPRIO Perfil de um jeito que remova a
// gravação em Configuração — senão a pessoa se tranca para fora da própria
// tela de Perfis (mesmo espírito da guarda de auto-lockout de `usuario.ts`).
// Perfil inativo já não concede nenhuma Permissão de Módulo (`_acesso.ts`),
// então "inativar o meu" seria um auto-lockout de verdade, não só cosmético.

import type { Snapshot } from "../domain/snapshot.ts";
import {
  criarPerfil,
  definirAtivoPerfil,
  editarPerfil,
  type PermissaoModuloEntrada,
} from "../domain/perfis.ts";
import { concedeGravacao, MODULOS, NIVEIS_PERMISSAO } from "../domain/schema.ts";
import { assertNunca } from "../lib/assert-nunca.ts";
import {
  comoObjeto,
  exigirBooleano,
  exigirTexto,
  lista,
  texto,
  umDe,
} from "./_forma.ts";
import type { ContextoComando } from "./lotes.ts";

export type ComandoPerfil =
  | { tipo: "criar"; nome: string; permissoes: PermissaoModuloEntrada[] }
  | { tipo: "editar"; id: string; nome: string; permissoes: PermissaoModuloEntrada[] }
  | { tipo: "definirAtivo"; id: string; ativo: boolean };

function aindaGravaConfiguracao(permissoes: PermissaoModuloEntrada[]): boolean {
  return permissoes.some((p) => p.modulo === "configuracao" && concedeGravacao(p.nivel));
}

export function executarComandoPerfil(
  snapshot: Snapshot,
  cmd: ComandoPerfil,
  ctx: ContextoComando,
): Snapshot {
  const meuPerfilId = snapshot.usuarios.find((u) => u.id === ctx.usuarioAtual)?.perfilId;
  switch (cmd.tipo) {
    case "criar":
      return criarPerfil(snapshot, { nome: cmd.nome, permissoes: cmd.permissoes });
    case "editar": {
      if (meuPerfilId === cmd.id && !aindaGravaConfiguracao(cmd.permissoes)) {
        throw new Error(
          "Você não pode remover a sua própria gravação em Configuração.",
        );
      }
      return editarPerfil(snapshot, cmd.id, { nome: cmd.nome, permissoes: cmd.permissoes });
    }
    case "definirAtivo":
      if (meuPerfilId === cmd.id && !cmd.ativo) {
        throw new Error("Você não pode inativar o seu próprio perfil.");
      }
      return definirAtivoPerfil(snapshot, cmd.id, cmd.ativo);
    default:
      return assertNunca(cmd);
  }
}

function validarPermissao(valor: unknown, i: number): PermissaoModuloEntrada {
  const p = comoObjeto(valor, `permissoes[${i}]`);
  return {
    modulo: umDe(p.modulo, `permissoes[${i}].modulo`, MODULOS),
    nivel: umDe(p.nivel, `permissoes[${i}].nivel`, NIVEIS_PERMISSAO),
  };
}

export function validarComandoPerfil(valor: unknown): ComandoPerfil {
  const c = comoObjeto(valor, "Comando de perfil");
  switch (c.tipo) {
    case "criar":
      return {
        tipo: "criar",
        nome: texto(c.nome),
        permissoes: lista(c.permissoes, "permissoes", validarPermissao),
      };
    case "editar":
      return {
        tipo: "editar",
        id: exigirTexto(c.id, "id"),
        nome: texto(c.nome),
        permissoes: lista(c.permissoes, "permissoes", validarPermissao),
      };
    case "definirAtivo":
      return {
        tipo: "definirAtivo",
        id: exigirTexto(c.id, "id"),
        ativo: exigirBooleano(c.ativo, "ativo"),
      };
    default:
      throw new Error(`Tipo de comando de perfil desconhecido: ${String(c.tipo)}.`);
  }
}
